import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Docked } from '@components/Docked';
import { Mothership } from '@components/Mothership';
import { HealthDisplay } from '@components/HealthDisplay';
import { TargetHighlight } from '@components/TargetHighlight';
import { Selectable } from '@components/Selectable';
import { BaseLayout, berthPosition, Point } from '@/base/BaseLayout';
import { getShipGeometry, isShipShape } from '@renderSystems/ShipShapes';
import { Effects } from '@/effects/Effects';

const UNDOCK_FRAMES = 34;
const LAUNCH_ACCELERATION = 0.22;
const LAUNCH_TOP_SPEED = 11;
const OFFSCREEN_MARGIN = 80;

/**
 * Keeps the base alive: the mothership drifts on a slow sway, docked ships bob in
 * their berths, and launched ships peel away from the hull and fly off screen.
 * Launching ships are moved by MovementSystem; this system only steers them.
 */
export class DockingSystem extends System {
  private frame = 0;

  constructor(private readonly layout: () => BaseLayout) {
    super();
  }

  /** The slow drift applied to the whole mothership group. */
  getSway(): Point {
    return {
      x: Math.sin(this.frame * 0.0045) * 7,
      y: Math.sin(this.frame * 0.0031 + 1.2) * 4.5,
    };
  }

  update(_deltaTime: number): void {
    this.frame++;
    const layout = this.layout();
    const sway = this.getSway();

    for (const mothership of this.getEntitiesWithComponents(Mothership, Position)) {
      const position = mothership.getComponent(Position)!;
      position.x = layout.center.x + sway.x;
      position.y = layout.center.y + sway.y;
      const velocity = mothership.getComponent(Velocity);
      if (velocity) velocity.angle = layout.angle + Math.sin(this.frame * 0.002) * 0.012;
    }

    for (const ship of this.getEntitiesWithComponents(Docked, Position, Velocity)) {
      const docked = ship.getComponent(Docked)!;
      const position = ship.getComponent(Position)!;
      const velocity = ship.getComponent(Velocity)!;

      if (docked.phase === 'docked' || (docked.phase === 'undocking' && docked.delay > 0)) {
        if (docked.phase === 'undocking') docked.delay--;
        this.holdInBerth(docked, position, velocity, layout, sway);
        continue;
      }

      const side = layout.berths[docked.berth]?.side ?? 1;
      docked.frame++;

      if (docked.phase === 'undocking') {
        // Swing out and away from the hull, easing off the clamps
        const t = docked.frame / UNDOCK_FRAMES;
        velocity.angle = layout.angle + side * 0.75 * (1 - t * 0.6);
        velocity.speed = Math.min(1.4, 0.2 + t * 1.4);
        if (docked.frame >= UNDOCK_FRAMES) {
          docked.phase = 'launching';
          docked.frame = 0;
        }
      } else if (docked.phase === 'launching') {
        // Straighten onto the mothership's heading and burn out of the system
        velocity.angle += (layout.angle - velocity.angle) * 0.08;
        velocity.speed = Math.min(LAUNCH_TOP_SPEED, velocity.speed + LAUNCH_ACCELERATION);
        this.emitTrail(ship.getComponent(Renderable), position, velocity);

        if (this.isOffscreen(position, layout)) {
          docked.phase = 'gone';
          velocity.speed = 0;
          const renderable = ship.getComponent(Renderable);
          if (renderable) renderable.alpha = 0;
          // Nothing left on screen to label or highlight
          ship.removeComponent(HealthDisplay);
          const highlight = ship.getComponent(TargetHighlight);
          if (highlight) highlight.isTargeted = false;
          const selectable = ship.getComponent(Selectable);
          if (selectable) selectable.selected = false;
        }
      }
    }
  }

  private holdInBerth(docked: Docked, position: Position, velocity: Velocity, layout: BaseLayout, sway: Point): void {
    const berth = berthPosition(layout, docked.berth, sway);
    const t = this.frame + docked.bobPhase * 60;
    const bob = Math.sin(t * 0.035) * 2.4;
    const drift = Math.sin(t * 0.013) * 1.4;
    const outward = layout.angle + ((layout.berths[docked.berth]?.side ?? 1) * Math.PI) / 2;

    position.x = berth.x + Math.cos(outward) * bob + Math.cos(layout.angle) * drift;
    position.y = berth.y + Math.sin(outward) * bob + Math.sin(layout.angle) * drift;
    velocity.angle = layout.angle + Math.sin(t * 0.021) * 0.035;
    velocity.speed = 0;
  }

  private emitTrail(renderable: Renderable | undefined, position: Position, velocity: Velocity): void {
    if (this.frame % 2 !== 0 || !renderable) return;
    const color = isShipShape(renderable.shape) ? getShipGeometry(renderable.shape).engineColor : renderable.color;
    const back = renderable.size * 0.9;
    Effects.launchTrail(
      this.world,
      position.x - Math.cos(velocity.angle) * back,
      position.y - Math.sin(velocity.angle) * back,
      velocity.angle,
      color
    );
  }

  private isOffscreen(position: Position, layout: BaseLayout): boolean {
    return position.x < -OFFSCREEN_MARGIN || position.y < -OFFSCREEN_MARGIN ||
      position.x > layout.width + OFFSCREEN_MARGIN || position.y > layout.height + OFFSCREEN_MARGIN;
  }
}
